const API_BASE = '/api';

function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;

    if (typeof err.error === 'string') {
      return err.error;
    }
    if (typeof err.message === 'string') {
      return err.message;
    }

    const issues = err.issues;
    if (Array.isArray(issues) && issues.length > 0) {
      const first = issues[0];
      if (first && typeof first === 'object' && typeof (first as Record<string, unknown>).message === 'string') {
        return (first as Record<string, unknown>).message as string;
      }
    }

    if (err.error && typeof err.error === 'object') {
      return extractErrorMessage(err.error);
    }
  }

  return 'Er is een fout opgetreden';
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  // Do not set Content-Type when the body is FormData — the browser must set
  // the multipart boundary automatically.
  const isFormData = options?.body instanceof FormData;
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    // CMS data must always represent the current server state. This also
    // protects the editor from an intermediary that ignores response headers.
    cache: 'no-store',
    credentials: 'include', // Send cookies with requests
    headers: isFormData
      ? options?.headers
      : {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
  });

  if (!response.ok) {
    // Handle unauthorized errors
    if (response.status === 401) {
      // Only dispatch unauthorized event for non-session-check endpoints
      // /auth/me is used to check existing sessions and should fail silently
      if (endpoint !== '/auth/me') {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      throw new Error('Niet geautoriseerd');
    }

    const error = await response.json().catch(() => ({ error: 'Onbekende fout' }));
    throw new Error(extractErrorMessage(error));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string) => fetchApi<T>(endpoint),
  post: <T>(endpoint: string, data: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  put: <T>(endpoint: string, data: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  patch: <T>(endpoint: string, data: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: <T>(endpoint: string) =>
    fetchApi<T>(endpoint, {
      method: 'DELETE',
    }),
  postForm: <T>(endpoint: string, body: FormData) =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      body,
    }),
};
